/**
 * Export Service
 * Handles exporting data to various formats (CSV, JSON, Excel-compatible)
 */

class ExportService {
  /**
   * Export data to CSV format
   */
  exportToCSV(data, filename = 'export') {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    const columns = Object.keys(data[0]);
    const csvRows = [];

    // Add header row
    csvRows.push(columns.map(col => this.escapeCSVValue(col)).join(','));

    // Add data rows
    for (const row of data) {
      const values = columns.map(col => this.escapeCSVValue(row[col]));
      csvRows.push(values.join(','));
    }

    const csvContent = csvRows.join('\n');
    this.downloadFile(csvContent, `${filename}.csv`, 'text/csv;charset=utf-8;');
  }

  /**
   * Export data to JSON format
   */
  exportToJSON(data, filename = 'export') {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    const jsonContent = JSON.stringify(data, null, 2);
    this.downloadFile(jsonContent, `${filename}.json`, 'application/json');
  }

  /**
   * Export data to Excel-compatible format (TSV that Excel can open)
   */
  exportToExcel(data, filename = 'export') {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    const columns = Object.keys(data[0]);
    const tsvRows = [];

    // Add header row
    tsvRows.push(columns.join('\t'));

    // Add data rows
    for (const row of data) {
      const values = columns.map(col => {
        const val = row[col];
        if (val === null || val === undefined) return '';
        return String(val).replace(/\t/g, ' ').replace(/\n/g, ' ');
      });
      tsvRows.push(values.join('\t'));
    }

    const tsvContent = tsvRows.join('\n');
    this.downloadFile(tsvContent, `${filename}.xls`, 'application/vnd.ms-excel');
  }

  /**
   * Export query results with metadata
   */
  exportQueryResults(data, query, metadata = {}) {
    const exportData = {
      query: query,
      exportedAt: new Date().toISOString(),
      rowCount: data.length,
      columns: data.length > 0 ? Object.keys(data[0]) : [],
      ...metadata,
      data: data
    };

    const filename = `query_results_${Date.now()}`;
    this.exportToJSON([exportData], filename);
  }

  /**
   * Generate a report combining multiple datasets
   */
  exportReport(sections, filename = 'report') {
    const report = {
      generatedAt: new Date().toISOString(),
      sections: sections.map(section => ({
        title: section.title,
        description: section.description,
        data: section.data,
        rowCount: section.data?.length || 0
      }))
    };

    this.downloadFile(JSON.stringify(report, null, 2), `${filename}.json`, 'application/json');
  }

  /**
   * Export a batch of query results as JSON or HTML report
   */
  exportBatchQueries({ title, datasetName, queries, filename = 'query_batch', format = 'json' }) {
    const sections = (queries || []).map((query, index) => {
      const rows = Array.isArray(query.results) ? query.results : [];
      const limit = 200;
      const truncated = rows.length > limit;
      const data = rows.slice(0, limit);
      const rowCount = query.row_count || rows.length;

      return {
        title: query.question || `Query ${index + 1}`,
        description: [
          datasetName ? `Dataset: ${datasetName}` : null,
          query.sql ? `SQL: ${query.sql}` : null,
          `Rows: ${rowCount}${truncated ? ` (showing first ${limit})` : ''}`
        ].filter(Boolean).join(' • '),
        data
      };
    });

    if (format === 'html') {
      this.exportReportHTML({
        title: title || 'DuckPilot Query Bundle',
        summary: datasetName ? `Dataset: ${datasetName}` : 'Saved queries export',
        sections,
        filename
      });
      return;
    }

    this.exportReport(sections, filename);
  }

  /**
   * Export report to a shareable HTML file
   */
  exportReportHTML({ title, summary, sections, filename = 'report' }) {
    const escapeHtml = (value) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    const sectionHtml = (sections || []).map((section) => {
      const rows = Array.isArray(section.data) ? section.data : [];
      const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
      const table = headers.length > 0 ? `
        <table>
          <thead>
            <tr>${headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${rows.map((row) => `
              <tr>${headers.map((h) => `<td>${escapeHtml(row[h])}</td>`).join('')}</tr>
            `).join('')}
          </tbody>
        </table>
      ` : `<div class="empty">No data available</div>`;

      return `
        <section>
          <h2>${escapeHtml(section.title)}</h2>
          ${section.description ? `<p>${escapeHtml(section.description)}</p>` : ''}
          ${table}
        </section>
      `;
    }).join('');

    const html = `
      <!doctype html>
      <html lang="en">
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${escapeHtml(title || 'DuckPilot Report')}</title>
          <style>
            :root {
              color-scheme: light;
              font-family: "IBM Plex Sans", "Inter", Arial, sans-serif;
              background: #f7f7f8;
              color: #111827;
            }
            body {
              margin: 0;
              padding: 32px;
              background: #f7f7f8;
            }
            header {
              margin-bottom: 24px;
            }
            h1 {
              font-size: 28px;
              margin: 0 0 8px;
            }
            .summary {
              background: #ffffff;
              border: 1px solid #e5e7eb;
              border-radius: 12px;
              padding: 16px;
              margin-bottom: 24px;
            }
            section {
              background: #ffffff;
              border: 1px solid #e5e7eb;
              border-radius: 12px;
              padding: 16px;
              margin-bottom: 16px;
            }
            h2 {
              margin: 0 0 8px;
              font-size: 18px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
            }
            th, td {
              padding: 8px;
              border-bottom: 1px solid #e5e7eb;
              text-align: left;
            }
            th {
              background: #f3f4f6;
              font-weight: 600;
            }
            .empty {
              color: #6b7280;
              font-size: 13px;
            }
          </style>
        </head>
        <body>
          <header>
            <h1>${escapeHtml(title || 'DuckPilot Report')}</h1>
            <div class="summary">
              <p>${escapeHtml(summary || 'Generated by DuckPilot')}</p>
              <p><strong>Generated:</strong> ${escapeHtml(new Date().toLocaleString())}</p>
            </div>
          </header>
          ${sectionHtml}
        </body>
      </html>
    `;

    this.downloadFile(html, `${filename}.html`, 'text/html');
  }

  // Helper methods
  escapeCSVValue(value) {
    if (value === null || value === undefined) {
      return '';
    }
    
    const stringValue = String(value);
    
    // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    
    return stringValue;
  }

  downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }

  /**
   * Get estimated file size for export
   */
  estimateExportSize(data, format = 'csv') {
    if (!data || data.length === 0) return '0 B';
    
    // Rough estimation based on format
    const sampleSize = JSON.stringify(data.slice(0, 10)).length;
    const estimatedTotal = (sampleSize / Math.min(10, data.length)) * data.length;
    
    const multiplier = format === 'json' ? 1.5 : 1;
    const bytes = estimatedTotal * multiplier;
    
    if (bytes < 1024) return `${bytes.toFixed(0)} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

const exportService = new ExportService();
export default exportService;
